import { NodeEulerRotationTrack } from './eulerrotationtrack.js';
import { NodeRotationTrack } from './rotationtrack.js';
import { NodeScaleTrack } from './scaletrack.js';
import { HumanoidBodyRig } from './skeleton.js';
import { NodeTranslationTrack } from './translationtrack.js';

function isNodeTransformTrack(track) {
    return track instanceof NodeRotationTrack || track instanceof NodeEulerRotationTrack || track instanceof NodeTranslationTrack || track instanceof NodeScaleTrack;
}
function cloneTrack(track) {
    const cloned = track.clone();
    cloned.name = track.name;
    cloned.target = track.target;
    cloned.jointIndex = track.jointIndex;
    return cloned;
}
function addJoint(set, rig, joint) {
    if (joint && rig.joints.includes(joint)) {
        set.add(joint);
    }
}
function addJointAndDescendants(set, rig, joint) {
    if (!joint) {
        return;
    }
    for (const candidate of rig.joints){
        if (joint === candidate || joint.isParentOf(candidate)) {
            set.add(candidate);
        }
    }
}
function removeJointAndDescendants(set, rig, joint) {
    if (!joint) {
        return;
    }
    for (const candidate of rig.joints){
        if (joint === candidate || joint.isParentOf(candidate)) {
            set.delete(candidate);
        }
    }
}
function addSemanticJoint(set, rig, joint, includeDescendants) {
    if (includeDescendants) {
        addJointAndDescendants(set, rig, joint);
    } else {
        addJoint(set, rig, joint);
    }
}
function removeSemanticJoint(set, rig, joint, includeDescendants) {
    if (includeDescendants) {
        removeJointAndDescendants(set, rig, joint);
    } else if (joint) {
        set.delete(joint);
    }
}
function addHumanoidPreset(set, rig, mapping, preset, boundary) {
    const body = mapping.body;
    switch(preset){
        case 'fullBody':
            for (const joint of rig.joints){
                set.add(joint);
            }
            break;
        case 'upperBody':
            addJointAndDescendants(set, rig, body[boundary ?? HumanoidBodyRig.Spine]);
            removeJointAndDescendants(set, rig, body[HumanoidBodyRig.LeftUpperLeg]);
            removeJointAndDescendants(set, rig, body[HumanoidBodyRig.RightUpperLeg]);
            break;
        case 'lowerBody':
            addJoint(set, rig, body[HumanoidBodyRig.Hips]);
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.LeftUpperLeg]);
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.RightUpperLeg]);
            break;
        case 'head':
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.Neck]);
            break;
        case 'arms':
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.LeftShoulder]);
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.RightShoulder]);
            break;
        case 'leftArm':
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.LeftShoulder]);
            break;
        case 'rightArm':
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.RightShoulder]);
            break;
        case 'legs':
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.LeftUpperLeg]);
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.RightUpperLeg]);
            break;
        case 'leftLeg':
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.LeftUpperLeg]);
            break;
        case 'rightLeg':
            addJointAndDescendants(set, rig, body[HumanoidBodyRig.RightUpperLeg]);
            break;
    }
}
function selectHumanoidJoints(rig, options) {
    const mapping = rig.humanoidJointMapping;
    if (!mapping) {
        console.error('createSkeletalMaskedAnimation: skeleton does not have a humanoid joint mapping');
        return null;
    }
    const selected = new Set();
    if (options.preset) {
        addHumanoidPreset(selected, rig, mapping, options.preset, options.boundary);
    } else if (!options.includeBody?.length && !options.includeLeftHand?.length && !options.includeRightHand?.length) {
        for (const joint of rig.joints){
            selected.add(joint);
        }
    }
    const includeDescendants = options.includeDescendants ?? true;
    for (const semantic of options.includeBody ?? []){
        addSemanticJoint(selected, rig, mapping.body[semantic], includeDescendants);
    }
    for (const semantic of options.includeLeftHand ?? []){
        addSemanticJoint(selected, rig, mapping.leftHand?.[semantic], includeDescendants);
    }
    for (const semantic of options.includeRightHand ?? []){
        addSemanticJoint(selected, rig, mapping.rightHand?.[semantic], includeDescendants);
    }
    for (const semantic of options.excludeBody ?? []){
        removeSemanticJoint(selected, rig, mapping.body[semantic], includeDescendants);
    }
    for (const semantic of options.excludeLeftHand ?? []){
        removeSemanticJoint(selected, rig, mapping.leftHand?.[semantic], includeDescendants);
    }
    for (const semantic of options.excludeRightHand ?? []){
        removeSemanticJoint(selected, rig, mapping.rightHand?.[semantic], includeDescendants);
    }
    return selected;
}
function matchesJointName(joint, matcher) {
    if (typeof matcher === 'string') {
        return joint.name === matcher;
    }
    if (matcher instanceof RegExp) {
        matcher.lastIndex = 0;
        return matcher.test(joint.name);
    }
    return matcher(joint);
}
function selectNamedJoints(rig, options) {
    const selected = new Set();
    const includeDescendants = options.includeDescendants ?? false;
    const included = options.include?.length ? rig.joints.filter((joint)=>options.include.some((matcher)=>matchesJointName(joint, matcher))) : rig.joints;
    for (const joint of included){
        if (includeDescendants) {
            addJointAndDescendants(selected, rig, joint);
        } else {
            selected.add(joint);
        }
    }
    for (const joint of rig.joints){
        if (options.exclude?.some((matcher)=>matchesJointName(joint, matcher))) {
            if (includeDescendants) {
                removeJointAndDescendants(selected, rig, joint);
            } else {
                selected.delete(joint);
            }
        }
    }
    return selected;
}
function getDefaultRootMotionMode(options) {
    if (options.rootMotion) {
        return options.rootMotion;
    }
    if (options.type === 'humanoid' && (options.preset === 'lowerBody' || options.preset === 'fullBody')) {
        return 'include';
    }
    return 'exclude';
}
function isRootMotionTrack(target, track, rig) {
    if (!(track instanceof NodeTranslationTrack)) {
        return false;
    }
    const hips = rig.humanoidJointMapping?.body[HumanoidBodyRig.Hips];
    return !!rig.rootJoint && target === rig.rootJoint || !!hips && target === hips;
}
function copySkeletonReferences(sourceClip, targetClip) {
    for (const id of sourceClip.skeletons){
        targetClip.addSkeleton(id);
    }
}
/** @internal */ function createSkeletalMaskedAnimationClip(animationSet, sourceClip, targetName, rig, options) {
    const selectedJoints = options.type === 'humanoid' ? selectHumanoidJoints(rig, options) : selectNamedJoints(rig, options);
    if (!selectedJoints) {
        return null;
    }
    const rootMotion = getDefaultRootMotionMode(options);
    const unsupportedTracks = options.unsupportedTracks ?? 'skip';
    const targetClip = animationSet.createAnimation(targetName);
    if (!targetClip) {
        return null;
    }
    targetClip.timeDuration = sourceClip.timeDuration;
    targetClip.weight = sourceClip.weight;
    targetClip.autoPlay = sourceClip.autoPlay;
    copySkeletonReferences(sourceClip, targetClip);
    for (const [target, tracks] of sourceClip.tracks){
        for (const track of tracks){
            if (!isNodeTransformTrack(track)) {
                if (unsupportedTracks === 'error') {
                    console.error(`createSkeletalMaskedAnimation: unsupported track type '${track.constructor.name}'`);
                    animationSet.deleteAnimation(targetName);
                    return null;
                }
                continue;
            }
            const rootMotionTrack = isRootMotionTrack(target, track, rig);
            const shouldCopy = rootMotion === 'only' ? rootMotionTrack : rootMotionTrack ? rootMotion === 'include' : selectedJoints.has(target);
            if (shouldCopy) {
                targetClip.addTrack(target, cloneTrack(track));
            }
        }
    }
    targetClip.timeDuration = sourceClip.timeDuration;
    return targetClip;
}

export { createSkeletalMaskedAnimationClip };
//# sourceMappingURL=animationmask.js.map
